import AbstractService from 'services/AbstractService';
import ContactService from 'services/ContactService';
import SecurityService from 'services/SecurityService';
import ProcessService from 'services/ProcessService';
import DataExplorerService from 'services/DataExplorerService';
import DataConsolidatorImportedDodDialog from 'components/core/DataConsolidatorImportedDodDialog';
import _ from 'lodash';
import React from "react";

const baseUrl = process.env.REACT_APP_BASE_URL;

class ModalsService extends AbstractService {
    constructor() {
        super();
        // MODAL TYPES
        this.TYPE_ERROR = 'error';
        this.TYPE_PROGRESS = 'progress';
        this.TYPE_STATUS_MESSAGE = 'status_message'; // non-modal status message
        this.TYPE_CONFIRM = 'confirm';
        this.TYPE_INFO = 'info';
        this.TYPE_CUSTOM = 'custom';
        this.TYPE_PERMISSIONS = 'permissions';
        this.TYPE_IMPORT_WIZARD = 'import_wizard';
        this.TYPE_VALIDATION = 'validation';
        this.TYPE_DOWNLOAD = 'download';
        this.TYPE_DATASET_VARIABLE = 'dataset_variable';

        // ACTION TYPES
        this.MODAL_OPEN = 'MODAL_OPEN';
        this.MODAL_CUSTOM_OPEN = 'MODAL_CUSTOM_OPEN';
        this.MODAL_CLOSE = 'MODAL_CLOSE';
    }

    getReducer() {
        return (state = this.getInitialState(), action) => {
            let actionType = action.type;
            let resource = 'resource';
            let newState = state;

            // First see if this is an API action that requires modals
            // We ignore dods, users, and owners fetching - let them run in the background
            if (!actionType.startsWith('DODS_FETCH_') && !actionType.startsWith('USERS_FETCH') &&
                !actionType.startsWith('PROCESS_OWNERS_FETCH') && !actionType.startsWith('PROCESS_OWNERS_SAVE')) {
                if (actionType.indexOf('FETCH_') > -1 || actionType.indexOf('SAVE_') > -1 || actionType.indexOf(
                    'DELETE_') > -1) {

                    //resource would be for example 'process'
                    resource = actionType.substring(0, actionType.indexOf('_')).toLowerCase();
                    if (action.processIds && action.processIds.length > 1) {
                        resource += 'es'; // we have multiple so make plural
                    }

                    //type would be for example FETCH_START
                    actionType = actionType.substring(actionType.indexOf('_') + 1, actionType.length);
                }
            }

            switch (actionType) {
                case 'FETCH_START':
                    let id = `Loading_${resource}`;
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, id, this.TYPE_PROGRESS, 'Loading', `Loading ${resource}...`));
                    break;

                case 'FETCH_ERROR':
                    // Remove the progress monitor from the state
                    id = `Loading_${resource}`;
                    let poppedState = this.popModal(state.openModals, id);

                    // Now add the error modal to the state
                    id = `FetchError_${resource}`;
                    let errorMsg = action.error;
                    if (action.error === 'Not Found' && action.processId) {
                        errorMsg = `Process '${action.processId}' does not exist.`;
                    }
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, id, this.TYPE_ERROR, 'Error', `Failed to load ${resource}.`, errorMsg));
                    break;

                case 'FETCH_SUCCESS':
                    id = `Loading_${resource}`;
                    newState = Object.assign({}, state, this.popModal(state.openModals, id));
                    break;

                case 'SAVE_START':
                    id = `Saving_${resource}`;
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, id, this.TYPE_PROGRESS, 'Saving', `Saving ${resource}...`));
                    break;

                case 'SAVE_ERROR':
                    id = `Saving_${resource}`;
                    poppedState = this.popModal(state.openModals, id);
                    id = `SaveError_${resource}`;
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, id, this.TYPE_ERROR, 'Error', `Failed to save ${resource}.`, action.error));
                    break;

                case 'SAVE_SUCCESS':
                    id = `Saving_${resource}`;
                    newState = Object.assign({}, state, this.popModal(state.openModals, id));
                    break;

                case 'DELETE_START':
                    id = `Deleting_${resource}`;
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, id, this.TYPE_PROGRESS, 'Deleting', `Deleting ${resource}...`));
                    break;

                case 'DELETE_ERROR':
                    id = `Deleting_${resource}`;
                    poppedState = this.popModal(state.openModals, id);
                    id = `DeleteError_${resource}`;
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, id, this.TYPE_ERROR, 'Error', `Failed to delete ${resource}.`, action.error));
                    break;

                case 'DELETE_SUCCESS':
                    id = `Deleting_${resource}`;
                    newState = Object.assign({}, state, this.popModal(state.openModals, id));
                    break;

                case SecurityService.LOGIN_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'login', this.TYPE_PROGRESS, 'Logging in', 'Logging in...'));
                    break;

                case SecurityService.LOGIN_SUCCESS:
                    newState = Object.assign({}, state, this.popModal(state.openModals, 'login'));
                    break;

                case SecurityService.LOGOUT_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'logout', this.TYPE_PROGRESS, 'Logging out', 'Logging out...'));
                    break;

                case SecurityService.LOGOUT_SUCCESS:
                    newState = Object.assign({}, state, this.popModal(state.openModals, 'logout'));
                    break;

                case SecurityService.BUILD_VERSION_UPDATED:
                    //TODO - maybe we need a new type for a closable modal that's not really an error?
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'build_version_updated', this.TYPE_ERROR, 'Refresh your browser',
                        'A newer version of PCM has been deployed.  Save any changes you wish to keep and refresh your browser now in order to prevent errors from occurring.'));
                    break;

                case SecurityService.LOGIN_ERROR:
                    poppedState = this.popModal(state.openModals, 'login');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'login_error', this.TYPE_ERROR, 'Error', 'Failed to log in.', action.error));
                    break;

                case ContactService.CONTACT_SEND_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'contact', this.TYPE_PROGRESS, 'Sending message', 'Sending message...'));
                    break;

                case ContactService.CONTACT_SEND_SUCCESS:
                    poppedState = this.popModal(state.openModals, 'contact');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'contact_success', this.TYPE_INFO, 'Message sent', 'Your message has been successfully sent. We will contact you very soon!'));
                    break;

                case ContactService.CONTACT_SEND_ERROR:
                    poppedState = this.popModal(state.openModals, 'contact');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'contact_error', this.TYPE_ERROR, 'Error', 'Failed to send message.', action.error));
                    break;

                case ProcessService.PROCESS_RUN_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'run', this.TYPE_PROGRESS, 'Submitting Process', 'Submitting process...'));
                    break;

                case ProcessService.PROCESS_RUN_SUCCESS:
                    poppedState = this.popModal(state.openModals, 'run');
                    const runDir = action.response.runDir;
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'run_success', this.TYPE_INFO, 'Process Submitted!',
                        `Thank you for using the PCM Data Consolidator.  Your processes is currently running at ${runDir}.  You will be emailed as soon as your data is ready to be retrieved.`));
                    break;

                case ProcessService.PROCESS_RUN_ERROR:
                    poppedState = this.popModal(state.openModals, 'run');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'run_error', this.TYPE_ERROR, 'Error', 'Failed to submit process.', action.error));
                    break;

                case ProcessService.PROCESS_IMPORT_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'import', this.TYPE_PROGRESS, 'Importing transform parameters', 'Importing transform parameters...'));
                    break;

                case ProcessService.PROCESS_IMPORT_SUCCESS:
                    newState = Object.assign({}, state, this.popModal(state.openModals, 'import'));
                    break;

                case ProcessService.PROCESS_IMPORT_ERROR:
                    poppedState = this.popModal(state.openModals, 'import');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'import_error', this.TYPE_ERROR, 'Error', 'Import failed.', action.error));
                    break;

                case ProcessService.PROCESS_VALIDATE_ERROR:
                    // The process validation runs in the background, so there is no progress modal to pop
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'vaidation_error',
                        this.TYPE_ERROR, 'Validation Error',
                        `Validation failed for process ${action.processId}.  This is not a critical error, and you can still save your process.  See console for more details.`));
                    break;

                case ProcessService.PROCESS_OWNERS_SAVE_START:
                    let permissionsModal = Object.assign({}, newState.permissionsModal, {
                        isSaving: true
                    });
                    newState = Object.assign({}, newState, { permissionsModal });
                    break;

                case ProcessService.PROCESS_OWNERS_SAVE_SUCCESS:
                    newState = Object.assign({}, state, {
                        permissionsModal: { open: false, processId: null, isSaving: false }
                    });
                    break;

                case ProcessService.PROCESS_OWNERS_SAVE_ERROR:
                    //clear isSaving state attribute
                    newState = Object.assign({}, state, {
                        permissionsModal: { open: false, processId: null, isSaving: false }
                    });
                    break;

                case ProcessService.PROCESS_CREATE_PROJECT_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'create', this.TYPE_PROGRESS, 'Creating project', 'Creating Project...'));
                    break;

                case ProcessService.PROCESS_CREATE_PROJECT_SUCCESS:
                    poppedState = this.popModal(state.openModals, 'create');

                    const modalConfig = {
                        id: 'create',
                        modalType: this.TYPE_DOWNLOAD,
                        modalTitle: 'Project created!',
                        modalMessage: 'Your project has been created. Click the button below to download your project tarball.',
                        downloadUrl: `${baseUrl}/processes/${action.processId}/get_project?
                            username=${action.username}&process_type=${action.processType}&project_name=${action.projectName}`
                    };

                    newState = Object.assign({}, state, this.pushModal(poppedState.openModals, modalConfig));
                    /*
                    } else {
                        const projectPath = action.response; // Response is project path if the user didn't choose to download

                        newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'create', this.TYPE_INFO, 'Project created!',
                            'The project has been downloaded to a temporary area from which you can move to your development area' +
                            ` using mv ${projectPath} /home/${action.username}/code.arm.gov/${action.processType}/${action.name}`)
                        );
                    }*/

                    break;

                case ProcessService.PROCESS_CREATE_PROJECT_ERROR:
                    poppedState = this.popModal(state.openModals, 'create');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'create_error', this.TYPE_ERROR, 'Error', 'Create project failed', action.error));
                    break;

                case 'DELETE_RUN_START':
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'delete-run', this.TYPE_PROGRESS, 'Deleting Run', 'Deleting data consolidator run...'));
                    break;

                case 'DELETE_RUN_SUCCESS':
                    newState = Object.assign({}, state, this.popModal(state.openModals, 'delete-run'));
                    break;

                case 'DELETE_RUN_ERROR':
                    poppedState = this.popModal(state.openModals, 'delete-run');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, 'delete-run-error', this.TYPE_ERROR, 'Error', 'Delete failed.', action.error));
                    break;

                case ProcessService.PROCESS_IMPORT_RUN_OUPUT_START:
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'import-dod-progress',
                       this.TYPE_PROGRESS, 'Importing DOD', `Importing DOD...`));
                    break;

                case ProcessService.PROCESS_IMPORT_RUN_OUPUT_SUCCESS:
                    const form = (
                       <DataConsolidatorImportedDodDialog
                          processId = {action.processId}
                          runDate = {action.runDate}
                          dod={action.dod}
                          version={action.version}
                       />
                    );
                    const config = {
                        id: 'import_dod_success',
                        modalType: this.TYPE_INFO,
                        modalTitle: 'DOD Created',
                        modalMessage: form,
                    }
                    poppedState = this.popModal(state.openModals, 'import-dod-progress');
                    newState = Object.assign({}, state, this.pushModal(poppedState.openModals, config));
                    break;

                case ProcessService.PROCESS_IMPORT_RUN_OUPUT_ERROR:
                    poppedState = this.popModal(state.openModals, 'import-run-output');
                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals,
                       'import-run-output-error', this.TYPE_ERROR, 'Error', `Could not import DOD ${action.dod}.`, action.error));
                    break;

                case DataExplorerService.DATASTREAMS_EXPLORE_START:
                    id = this._getDatastreamExploreId(action.processId);
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, id, this.TYPE_STATUS_MESSAGE, 'Loading', 'Exploring Data...'));
                    break;

                case DataExplorerService.DATASTREAMS_POLL_SUCCESS:

                    if (action.datastreams) {
                        const datastreamNames = Object.keys(action.datastreams);
                        // Only remove the dialog if all the pending requests are complete
                        if (!DataExplorerService.hasPendingRequestsForProcess(action.processId, datastreamNames)) {
                            id = this._getDatastreamExploreId(action.processId);
                            newState = Object.assign({}, state, this.popModal(state.openModals, id));
                        }
                    }
                    break;

                case DataExplorerService.DATASTREAMS_EXPLORE_ERROR:
                    id = this._getDatastreamExploreId(action.processId);
                    poppedState = this.popModal(state.openModals, id);
                    errorMsg = action.error;
                    if (typeof action.error === 'object') {
                        errorMsg = action.error.message;
                    }
                    // TODO: this needs to come from the server, but the fetch api is only returning
                    // 'Internal Server Error', so I am hardcoding the message here for now.
                    const errorDetails = 'The data explorer is temporarily disabled due to technical\n' +
                            'difficulties accessing files in /data/archive.\n' +
                            'If you need to set transformation parameters, please use\n' +
                            'the development server at https://mars.dmf.arm.gov/pcm-process-dev\n';

                    newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, id, this.TYPE_ERROR,
                        'Data Explorer Error',
                        'Failed to run data explorer.  Note that this will not affect the process, just the ability to validate transform parameters.',
                        errorDetails));
                    break;

                case DataExplorerService.DATASTREAMS_POLL_ERROR:
                    errorMsg = action.error;
                    if (typeof action.error === 'object') {
                        errorMsg = action.error.message;
                    }

                    if (action.pendingRequests) {
                        Object.keys(action.pendingRequests).forEach(processId => {
                            id = this._getDatastreamExploreId(processId);
                            poppedState = this.popModal(state.openModals, id);
                            newState = Object.assign({}, state, this.addModalToState(poppedState.openModals, id, this.TYPE_ERROR,
                                'Data Explorer Error',
                                `Failed to run data explorer for process ${processId}.  Note that this will not affect the process, just the ability to validate transform parameters.`,
                                errorMsg));
                        });
                    }
                    if (action.requestsToCancel) {
                        console.log('Failed to cancel some outdated poll requests:');
                        console.log(JSON.stringify(action.requestsToCancel, null, 2));
                        console.log(errorMsg);
                    }

                    break;

                case 'OWNERS_SAVE_ERROR':
                    newState = Object.assign({}, state, this.addModalToState(state.openModals, 'owners_error', this.TYPE_ERROR, 'Error', 'Failed to save permissions.', action.error));
                    break;

                case this.MODAL_OPEN:
                    const modalId = action.payload.id;
                    if (modalId === this.TYPE_PERMISSIONS) {
                        permissionsModal = Object.assign({}, newState.permissionsModal, {
                            open: true,
                            processId: action.payload.processId
                        });
                        newState = Object.assign({}, newState, { permissionsModal });

                    } else {
                        newState = Object.assign({}, state, this.pushModal(state.openModals, action.payload));
                    }
                    break;

                case this.MODAL_CLOSE:
                    if (action.id === this.TYPE_PERMISSIONS) {
                        newState = Object.assign({}, newState, {
                            permissionsModal: { open: false, processId: null }
                        });
                    } else {
                        newState = Object.assign({}, state, this.popModal(state.openModals, action.id));
                    }

                    break;

                default:
                    return newState;
            }

            return newState;
        };
    }

    getInitialState() {
        return {
            openModals: [],
            showWizardModal: false,
            permissionsModal: { open: false, processId: null, isSaving: false }
        };
    }

    getStoreKey() {
        return 'modals';
    }

    pushModal(openModals, modalConfig) {
        // Spread syntax will make a copy of the existing array
        const newOpenModals = [...(openModals), modalConfig]; // push to end
        return { openModals: newOpenModals };
    }

    popModal(openModals, id) {
        let indexToPop = null;
        openModals.forEach((openModal, index) => {
            if (openModal.id === id) {
                indexToPop = index;
            }
        });
        const newOpenModals = [...(openModals)];
        if (indexToPop !== null) {
            newOpenModals.splice(indexToPop, 1);
        }
        return { openModals: newOpenModals };
    }

    addModalToState(openModals, id, modalType, modalTitle, modalMessage, errorDetails) {
        const modalConfig = { id, modalType, modalTitle, modalMessage, errorDetails };
        return this.pushModal(openModals, modalConfig);
    }

    /**
     * UI components can call this to pop up a modal dialog.
     * @param modalConfig
     */
    openModal = (modalConfig) => {
        const action = {
            type: this.MODAL_OPEN,
            payload: modalConfig
        };
        this.dispatch(action);
    };

    /**
     * UI components can call this to close the top modal on the stack.
     */
    closeModal = (id) => {
        const action = {
            id,
            type: this.MODAL_CLOSE
        };
        this.dispatch(action);
    };

    _getDatastreamExploreId = (processId) => {
        return `Exploring_Datastreams_${processId}`;
    };

}

export default new ModalsService();



// WEBPACK FOOTER //
// ./src/services/ModalsService.js